/**
 * BgDataFlow — Flowing data particles + connection traces
 * Registro: AI/tech/data — para cenas de prova, alcance, números
 * Paleta: teal nodes + amber accents em fundo near-black
 */
import React from "react";
import {AbsoluteFill, useCurrentFrame, useVideoConfig} from "remotion";

interface BgDataFlowProps {
  /** Cor primária dos nós (default: teal) */
  nodeColor?: string;
  /** Intensidade geral 0–1 (default: 0.7) */
  intensity?: number;
  /** Velocidade do fluxo (default: 1) */
  speed?: number;
}

export const BgDataFlow: React.FC<BgDataFlowProps> = ({
  nodeColor = "#14B8A6",
  intensity = 0.7,
  speed = 1,
}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();
  const t = (f / fps) * speed;

  // 18 particles em golden ratio distribution
  const particles = Array.from({length: 18}, (_, i) => {
    const baseX = (i * 137.508) % 100;
    const baseY = (i * 73.211) % 100;
    // Flow upward + slight horizontal drift
    const flowY = ((baseY - t * 8 * (0.6 + (i % 3) * 0.2)) + 200) % 100;
    const flowX = baseX + Math.sin(t * 0.4 + i * 0.9) * 2.5;
    const size = 2 + (i % 4);
    const opacity = (0.25 + (i % 3) * 0.12) * intensity;
    const isAccent = i % 5 === 0;
    return {x: flowX, y: flowY, size, opacity, isAccent};
  });

  // 8 connection traces (horizontal data lines)
  const traces = Array.from({length: 8}, (_, i) => {
    const y = 10 + i * 11.5;
    const offset = (t * 60 * (0.5 + (i % 3) * 0.3) + i * 47) % 120;
    const width = 15 + (i % 4) * 8;
    const opacity = (0.06 + (i % 3) * 0.03) * intensity;
    return {y, offset, width, opacity};
  });

  return (
    <AbsoluteFill style={{background: "#080808", overflow: "hidden"}}>
      {/* Ambient glow blob */}
      <div style={{
        position: "absolute",
        left: `${30 + Math.sin(t * 0.2) * 10}%`,
        top: `${40 + Math.cos(t * 0.15) * 8}%`,
        width: "65%", height: "65%",
        transform: "translate(-50%,-50%)",
        background: `radial-gradient(circle, ${nodeColor}18 0%, transparent 70%)`,
        filter: "blur(80px)",
        willChange: "left, top",
      }}/>
      {/* Horizontal trace lines */}
      {traces.map((tr, i) => (
        <div key={i} style={{
          position: "absolute",
          left: `${tr.offset - tr.width}%`,
          top: `${tr.y}%`,
          width: `${tr.width}%`,
          height: 1,
          background: `linear-gradient(90deg, transparent 0%, ${nodeColor} 40%, ${nodeColor} 60%, transparent 100%)`,
          opacity: tr.opacity,
          willChange: "left",
        }}/>
      ))}
      {/* Data nodes */}
      {particles.map((p, i) => (
        <div key={i} style={{
          position: "absolute",
          left: `${p.x}%`,
          top: `${p.y}%`,
          width: p.size,
          height: p.size,
          borderRadius: "50%",
          background: p.isAccent ? "#F59E0B" : nodeColor,
          opacity: p.opacity,
          willChange: "left, top",
          pointerEvents: "none",
        }}/>
      ))}
      {/* Vignette */}
      <div style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(0,0,0,0.75) 100%)",
        pointerEvents: "none",
      }}/>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
